/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/lines-between-class-members */

import { GTexturePool } from "../core/texturePool";
import { GetVec3, Vector3 } from "../core/types";
import { GTime, MathClamp, MathMapToRange } from "../core/utils";
import { GSimpleShapesRenderer } from "../helpers/shapeRender";

function GetUniformParametersList(gl: WebGL2RenderingContext, shaderProgram: WebGLProgram) {
    const params = {
        Position: gl.getUniformLocation(shaderProgram, "Position"),
        Velocity: gl.getUniformLocation(shaderProgram, "Velocity"),
        FadeInOutParameters: gl.getUniformLocation(shaderProgram, "FadeInOutParameters"),
        Time: gl.getUniformLocation(shaderProgram, "Time"),
        ScreenRatio: gl.getUniformLocation(shaderProgram, "ScreenRatio"),
        CameraDesc: gl.getUniformLocation(shaderProgram, "CameraDesc"),
        AnimationFrameIndex: gl.getUniformLocation(shaderProgram, "AnimationFrameIndex"),
        ColorTexture: gl.getUniformLocation(shaderProgram, "ColorTexture"),
        LaserTexture: gl.getUniformLocation(shaderProgram, "LaserTexture"),
        NoiseTexture: gl.getUniformLocation(shaderProgram, "NoiseTexture"),
        LUTTexture: gl.getUniformLocation(shaderProgram, "LUTTexture"),
        PositionStart: gl.getUniformLocation(shaderProgram, "PositionStart"),
        PositionEnd: gl.getUniformLocation(shaderProgram, "PositionEnd"),
        SpotlightScale: gl.getUniformLocation(shaderProgram, "SpotlightScale"),
        SpotlightPos: gl.getUniformLocation(shaderProgram, "SpotlightPos"),
        SpotlightTexture: gl.getUniformLocation(shaderProgram, "SpotlightTexture"),
        ColorScale: gl.getUniformLocation(shaderProgram, "ColorScale"),
        LineThickness: gl.getUniformLocation(shaderProgram, "LineThickness"),
        LineColorCutThreshold: gl.getUniformLocation(shaderProgram, "LineColorCutThreshold"),
        Orientation: gl.getUniformLocation(shaderProgram, "Orientation"),
        Scale: gl.getUniformLocation(shaderProgram, "Scale"),
        Color: gl.getUniformLocation(shaderProgram, "Color"),
        CurrentState: gl.getUniformLocation(shaderProgram, "CurrentState"),
        LineLength: gl.getUniformLocation(shaderProgram, "LineLength"),

        LineEnd: gl.getUniformLocation(shaderProgram, "LineEnd"),
    };
    return params;
}

class CAnimationComponent {
    AgeGlobal = 0.0;
    Age = 0.0;
    AgeNormalized = 0.0;

    FadeInParameter = 1.0;
    FadeInSpeed = 1.0;

    FadeOutParameter = 0.0;
    FadeOutSpeed = 1.0;

    Lifetime = 1.0;

    Speed = 1.0;

    FlipbookSize = { x: 16, y: 4 };

    Update() {
        this.AgeGlobal += GTime.Delta;
        this.Age += GTime.Delta * this.Speed;
        this.Age = this.Age % this.Lifetime;
        this.AgeNormalized = this.Age / this.Lifetime;
    }

    FadeInUpdate() {
        if (this.FadeInParameter < 1.0) {
            this.FadeInParameter += GTime.Delta * this.FadeInSpeed;
            this.FadeInParameter = Math.min(1.0, this.FadeInParameter);
        }
    }

    FadeOutUpdate() {
        if (this.FadeOutParameter > 0) {
            this.FadeOutParameter -= GTime.Delta * this.FadeOutSpeed;
            this.FadeOutParameter = Math.max(0.0, this.FadeOutParameter);
        }
    }

    Reset() {
        this.AgeGlobal = 0.0;
        this.Age = 0.0;
        this.AgeNormalized = 0.0;
        this.FadeInParameter = 0.0;
        this.FadeOutParameter = 1.0;
    }

    IsFadeInFinished() {
        return this.FadeInParameter >= 0.99;
    }

    IsFadeOutFinished() {
        return this.FadeOutParameter <= 0.01;
    }
}

enum ELifetimeState {
    Inactive = 0,
    Attack = 1,
    Sustain,
    Release,
}

class CLifetimeComponent {
    AttackDuration = 0.1; //Fade In
    bOneShot = false;
    SustainDuration = 0.0; //Used if bOneShot
    ReleaseDuration = 1.0; //Fade Out

    TimeSinceStart = 0.0; //Global Age
    ParamT = 0.0; //ASR timeline
    StateCurrent = ELifetimeState.Attack;
    ValueCurrent = 0.0; //[0, 1]

    constructor(boneShot: boolean) {
        this.bOneShot = boneShot;

        this.TimeSinceStart = 1000.0;
        this.ParamT = 1000.0;
    }

    GetCurStateAndValue() {
        if (this.ParamT < this.AttackDuration) {
            const t = MathMapToRange(this.ParamT, 0.0, this.AttackDuration, 0.0, 1.0);
            return { state: ELifetimeState.Attack, value: t };
        }
        if (this.ParamT < this.AttackDuration + this.SustainDuration) {
            const t = 1;
            return { state: ELifetimeState.Sustain, value: t };
        }
        if (this.ParamT < this.AttackDuration + this.SustainDuration + this.ReleaseDuration) {
            const t = MathMapToRange(
                this.ParamT,
                this.AttackDuration + this.SustainDuration,
                this.AttackDuration + this.SustainDuration + this.ReleaseDuration,
                1.0,
                0.0,
            );
            return { state: ELifetimeState.Attack, value: t };
        }
        return { state: ELifetimeState.Inactive, value: 0 };
    }

    GetAttackParam() {
        const stateNParam = this.GetCurStateAndValue();
        if (stateNParam.state == ELifetimeState.Attack) {
            return stateNParam.value;
        } else {
            return 1.0;
        }
    }
    GetReleaseParam() {
        const stateNParam = this.GetCurStateAndValue();
        if (stateNParam.state == ELifetimeState.Release) {
            return stateNParam.value;
        } else {
            return 1.0;
        }
    }

    Reset() {
        this.TimeSinceStart = 0.0;
        this.ParamT = 0.0;
        this.ValueCurrent = 0.0;
        this.StateCurrent = ELifetimeState.Attack;
    }

    Update(bActive: boolean) {
        this.TimeSinceStart += GTime.Delta;
        // eslint-disable-next-line prefer-const
        let bUpdateASR = false;
        if (this.bOneShot) {
            bUpdateASR = true;
        } else {
            if (this.ParamT < this.AttackDuration) {
                bUpdateASR = true;
            } else if (!bActive) {
                bUpdateASR = true;
            }
        }
        if (bUpdateASR) {
            this.ParamT += GTime.Delta;
        }

        const statenParam = this.GetCurStateAndValue();
        this.ValueCurrent = statenParam.value;
        this.StateCurrent = statenParam.state;
    }
}

export enum EBurningTool {
    Laser = 0,
    Lighter,
    Thunder,
    Fireball,
    //===
    NUM,
}

export class CImpactComponentBase {
    //Resources
    LightFlareTexture!: WebGLTexture;
    LightFlareTexture1!: WebGLTexture;
    LightFlareTexture2!: WebGLTexture;
    LightFlareTexture3!: WebGLTexture;

    IntersectionPosWS: Vector3 = new Vector3(0, 0, 0);
    StartTime = 0;
    TimeSinceStart = 60;
    RndNumOnInit = 0;
    bMainImpactTriggered = false;

    LoadResources(gl: WebGL2RenderingContext) {
        this.LightFlareTexture = GTexturePool.CreateTexture(gl, false, `flareLight`);
        this.LightFlareTexture1 = GTexturePool.CreateTexture(gl, false, `flareHalo1`);
        this.LightFlareTexture2 = GTexturePool.CreateTexture(gl, false, `flareDispers0`);
        this.LightFlareTexture3 = GTexturePool.CreateTexture(gl, false, `flareFlash0`);
    }

    OnHit(posWS: Vector3) {
        this.IntersectionPosWS.Set(posWS);
        this.RndNumOnInit = Math.random();
        this.StartTime = GTime.Cur;
        this.TimeSinceStart = 0.0;
        this.bMainImpactTriggered = false;
    }

    UpdateAndRender(gl: WebGL2RenderingContext, color: Vector3) {
        //const timeSinceStart = GTime.Cur - this.StartTime;
        const timeSinceStart = this.TimeSinceStart;

        if (timeSinceStart < 10) {
            gl.enable(gl.BLEND);
            gl.blendEquation(gl.FUNC_ADD);
            gl.blendFunc(gl.ONE, gl.ONE);

            {
                let t = 0.0;
                const sustainThres = 0.05 * 3.0;
                const endThres = sustainThres + 0.7;
                if (timeSinceStart < sustainThres) {
                    t = 1.0;
                } else if (timeSinceStart < endThres) {
                    t = MathMapToRange(timeSinceStart, sustainThres, endThres, 1.0, 0.0);
                }

                const sizeScale = 1.0 * MathClamp(t, 0.0, 1.0) * (0.8 + (Math.sin(GTime.Cur * 12.0) + 1.0) * 0.1);

                const finalGlareSize = 2.25 * sizeScale;

                //Lens Flare
                {
                    const colorCur = GetVec3(0, 0, 0);
                    colorCur.Set(color);
                    colorCur.Mul(2.0 * Math.max(0.5, t));

                    GSimpleShapesRenderer.GInstance!.RenderPoint(
                        gl,
                        this.IntersectionPosWS,
                        GetVec3(finalGlareSize * 10.0, finalGlareSize, 0.0),
                        colorCur,
                        false,
                        0.0,
                        this.LightFlareTexture,
                    );
                }
            }

            //Halo rainbow
            {
                let t = 0.0;
                const startThres = 0.5;
                if (timeSinceStart < startThres) {
                    t = MathMapToRange(timeSinceStart, 0.0, startThres, 0.5, 1.0);
                }

                const colorCur = GetVec3(0, 0, 0);
                colorCur.Set(color);
                colorCur.Mul(2.0 - t * 1.5);

                GSimpleShapesRenderer.GInstance!.RenderPoint(
                    gl,
                    this.IntersectionPosWS,
                    t * 2.25,
                    colorCur,
                    false,
                    Math.PI * this.RndNumOnInit * 2.0,
                    this.LightFlareTexture2,
                );
            }

            //Halo fast
            {
                let t = 0.0;
                const startThres = 0.2;
                if (timeSinceStart < startThres) {
                    t = MathMapToRange(timeSinceStart, 0.0, startThres, 0.2, 1.0);
                }

                const colorCur = GetVec3(0, 0, 0);
                colorCur.Set(color);
                colorCur.Mul(2.0 - t * 1.5);

                GSimpleShapesRenderer.GInstance!.RenderPoint(
                    gl,
                    this.IntersectionPosWS,
                    t * 1.25,
                    colorCur,
                    false,
                    0.0,
                    this.LightFlareTexture1,
                );
            }

            //Flash
            {
                let t = 0.0;
                const startThres = 0.1;
                //if (owner.bActiveThisFrame && timeSinceStart < startThres) {
                if (timeSinceStart < startThres) {
                    t = 1.0;
                }

                const colorCur = GetVec3(0, 0, 0);
                colorCur.Set(color);
                colorCur.Mul(2.0 * t);

                GSimpleShapesRenderer.GInstance!.RenderPoint(
                    gl,
                    this.IntersectionPosWS,
                    1.8,
                    colorCur,
                    false,
                    0.0,
                    this.LightFlareTexture3,
                );
            }

            gl.disable(gl.BLEND);
        }

        //
        this.TimeSinceStart += GTime.Delta * GTime.DeltaTimeScale;
    }
}
